import { useMemo, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import PostRow from "@/components/feature/PostRow";
import { posts } from "@/mocks/posts";
import usePageMeta from "@/hooks/usePageMeta";

type TabKey = "hot" | "new" | "essence";

const tabs: Array<{ key: TabKey; label: string; icon: string }> = [
  { key: "hot", label: "热门话题", icon: "ri-fire-line" },
  { key: "new", label: "新帖", icon: "ri-time-line" },
  { key: "essence", label: "精华帖", icon: "ri-award-line" },
];

const rules = [
  "先说边界，再开始互动，任何时候都可以停下来。",
  "不发布露骨、冒犯或涉及未成年的内容。",
  "不同意见请温和表达，管理员会处理越界内容。",
];

export default function CommunityPage() {
  usePageMeta(
    "社区交流 - 挠脚心网站",
    "进入挠脚心网站社区交流版块，浏览热门话题、最新帖子与精华讨论，和同频的朋友一起聊聊轻松有趣的体验。",
    "社区交流,挠脚心社区,趣味话题,经验分享,轻松讨论"
  );

  const [tab, setTab] = useState<TabKey>("hot");
  const [visibleCount, setVisibleCount] = useState(5);
  const [showModal, setShowModal] = useState(false);

  const list = useMemo(() => {
    if (tab === "essence") {
      return posts.filter((item) => item.essence).sort((a, b) => b.heat - a.heat);
    }
    if (tab === "new") {
      return [...posts].sort((a, b) => (a.date < b.date ? 1 : -1));
    }
    return [...posts].sort((a, b) => b.heat - a.heat);
  }, [tab]);

  const visible = list.slice(0, visibleCount);
  const hasMore = visibleCount < list.length;

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHeader
          crumbs={[{ label: "首页", to: "/" }, { label: "社区交流" }]}
          kicker="Community"
          title="社区交流"
          description="这里是讨论区，不是表演区。分享真实体验、提入门问题、交换道具心得，都可以慢慢说。"
          aside={
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600"
            >
              <i className="ri-quill-pen-line text-base"></i>
              我要发帖
            </button>
          }
        />

        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                  {tabs.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setTab(item.key);
                        setVisibleCount(5);
                      }}
                      className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors md:text-sm ${
                        tab === item.key
                          ? "bg-background-50 font-medium text-foreground-950"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      <i className={`${item.icon} text-sm`}></i>
                      {item.label}
                    </button>
                  ))}
                </div>
                <span className="ml-auto text-xs text-foreground-500">共 {list.length} 个话题</span>
              </div>

              {visible.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {visible.map((post, index) => (
                    <PostRow key={post.id} post={post} index={index} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="ri-chat-smile-2-line"
                  title="暂无相关帖子，快来发起第一个话题吧！"
                  description="当前演示站点未开放发帖功能，你可以先看看其他分类里的讨论。"
                  actionLabel="看看热门话题"
                  onAction={() => {
                    setTab("hot");
                    setVisibleCount(5);
                  }}
                />
              )}

              <div className="mt-6 flex flex-col items-center gap-2">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((prev) => prev + 5)}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-arrow-down-line text-base"></i>
                    加载更多话题
                  </button>
                ) : (
                  <p className="text-xs text-foreground-400">已经看到全部话题了 · 演示数据</p>
                )}
              </div>
            </div>

            <aside className="flex flex-col gap-4 lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                    <i className="ri-shield-check-line text-sm"></i>
                  </span>
                  社区小公约
                </h2>
                <ul className="mt-4 flex flex-col gap-3">
                  {rules.map((rule, index) => (
                    <li key={rule} className="flex gap-2 text-xs leading-relaxed text-foreground-700">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-background-50 text-[10px] text-secondary-700">
                        {index + 1}
                      </span>
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                    <i className="ri-hashtag text-sm"></i>
                  </span>
                  热门话题标签
                </h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["新手入门", "道具心得", "边界沟通", "轻松体验", "冷知识", "周末游戏"].map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
                <p className="mt-4 border-t border-background-100 pt-3 text-xs leading-relaxed text-foreground-500">
                  提示：这是演示站点，社区内容为虚构示例，暂不支持真实发帖与回复。
                </p>
              </div>

              <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                <p className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                  <i className="ri-lightbulb-line text-base text-primary-700"></i>
                  第一次来？
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  先读一读《和好友玩挠痒游戏前，先说好这三条边界》，再决定要不要参与讨论。
                </p>
                <button
                  type="button"
                  onClick={() => setShowModal(true)}
                  className="mt-3 flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-foreground-900 px-3 py-2 text-xs text-background-50 transition-colors hover:bg-foreground-800"
                >
                  了解发帖规则
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />

      {showModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="post-modal-title"
          onClick={() => setShowModal(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-background-50 p-6 animate-fade-up"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-800">
                <i className="ri-lock-2-line text-lg"></i>
              </span>
              <button
                type="button"
                aria-label="关闭弹窗"
                onClick={() => setShowModal(false)}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-800"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <h2 id="post-modal-title" className="mt-4 font-heading text-lg text-foreground-950">
              发帖需要登录
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              当前是演示站点，暂未开放登录与注册，因此还不能真实发布帖子。你可以先浏览现有话题，感受一下社区的氛围。
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600"
              >
                先随便逛逛
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-full cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}